/**
 * The first-day checklist (step 3a of the owner's plan, 2026-09-26).
 *
 * Of eight shops, five never got to real selling. Each step here is a wall a
 * new shop hits in the first hour — no company code on the receipt, no
 * category to put a product in, no cashier to open a shift with, the screen
 * lock on the default PIN — and each is DETECTED from the shop's own data,
 * never ticked by hand: a step is done when the thing exists.
 *
 * Shown only to a young shop (fewer than YOUNG_SALES sales) that has not
 * dismissed it, so a shop that has been selling for months never sees it.
 */

import { t } from './i18n'

export const YOUNG_SALES = 20
export const FIRST_PRODUCTS = 5

export type FirstDayInput = {
  companyName: string
  companyId: string
  categories: number
  products: number
  cashiers: number
  pinIsDefault: boolean
  sales: number
  /** 3b — the template the shop chose; adds the one step its kind needs. */
  shopType?: string
  /** Products sold by the kilogram. */
  weighed?: number
  /** „თანმხლები (კომბო)" pairs — a coffee and its cup. */
  combos?: number
  /** Products carrying an expiry date. */
  withExpiry?: number
  /** 3c — models with sizes (112). */
  models?: number
  /** 1.55 — the shop's customers (ნისია). */
  customers?: number
  /** 1.55 — service products (169). */
  services?: number
  /** 1.55 — supplier codes linked by an RS.GE receive (078). */
  supplierCodes?: number
  /** 1.55 — products sold by the metre, kilogram or litre. */
  measured?: number
}

export type FirstDayStep = {
  key: 'company' | 'categories' | 'products' | 'profile' | 'cashier' | 'pin' | 'sale'
  label: string
  hint: string
  /** Where the step is done. */
  href: string
  done: boolean
  /** 1.55 — a suggestion, not a wall: ticked when done, never counted, never keeps the banner open. */
  optional?: true
}

/**
 * The one step a shop of this kind needs beyond the common six (3b): the
 * thing its shelves cannot sell without. Detected like the rest.
 */
function profileStep(i: FirstDayInput): FirstDayStep | null {
  switch (i.shopType) {
    case 'produce':
      return { key: 'profile', label: t('fd.produce'), hint: t('fd.produce.hint'), href: '/warehouse', done: (i.weighed ?? 0) > 0 }
    // 1.40 — weighed on a mechanical scale: no PLU, the till asks „რამდენი?“
    case 'poultry':
      return { key: 'profile', label: t('fd.produce'), hint: t('fd.poultry.hint'), href: '/warehouse', done: (i.weighed ?? 0) > 0 }
    case 'coffee':
    case 'kiosk':
      return { key: 'profile', label: t('fd.combo'), hint: t('fd.combo.hint'), href: '/warehouse', done: (i.combos ?? 0) > 0 }
    case 'cosmetics':
      return { key: 'profile', label: t('fd.expiry'), hint: t('fd.expiry.hint'), href: '/warehouse', done: (i.withExpiry ?? 0) > 0 }
    case 'clothing':
      return { key: 'profile', label: t('fd.model'), hint: t('fd.model.hint'), href: '/warehouse', done: (i.models ?? 0) > 0 }
    // 1.55 — seven kinds get a SUGGESTION: what most shops of the kind use, not what every
    // one of them cannot sell without (a stationery shop without a copier, a grocery that
    // never sells on credit), so it is optional and the checklist still never lies.
    case 'grocery':
      return { key: 'profile', label: t('fd.credit'), hint: t('fd.credit.hint'), href: '/credit', done: (i.customers ?? 0) > 0, optional: true }
    case 'household':
      return { key: 'profile', label: t('fd.catalog'), hint: t('fd.catalog.hint'), href: '/warehouse', done: i.products >= 30, optional: true }
    case 'autoparts':
      return { key: 'profile', label: t('fd.supplierCode'), hint: t('fd.supplierCode.hint'), href: '/suppliers', done: (i.supplierCodes ?? 0) > 0, optional: true }
    case 'petshop':
      return { key: 'profile', label: t('fd.feed'), hint: t('fd.feed.hint'), href: '/warehouse', done: (i.weighed ?? 0) > 0, optional: true }
    case 'accessories':
      return { key: 'profile', label: t('fd.service'), hint: t('fd.service.accessories.hint'), href: '/warehouse', done: (i.services ?? 0) > 0, optional: true }
    case 'stationery':
      return { key: 'profile', label: t('fd.service'), hint: t('fd.service.stationery.hint'), href: '/warehouse', done: (i.services ?? 0) > 0, optional: true }
    case 'hardware':
      return { key: 'profile', label: t('fd.measured'), hint: t('fd.measured.hint'), href: '/warehouse', done: (i.measured ?? 0) > 0, optional: true }
    default:
      return null
  }
}

export function firstDaySteps(i: FirstDayInput): FirstDayStep[] {
  const profile = profileStep(i)
  return [
    {
      key: 'company', label: t('fd.company'), hint: t('fd.company.hint'),
      href: '/settings?s=company', done: !!i.companyName.trim() && !!i.companyId.trim(),
    },
    {
      key: 'categories', label: t('fd.categories'), hint: t('fd.categories.hint'),
      href: '/warehouse', done: i.categories > 0,
    },
    {
      key: 'products', label: t('fd.products', { n: FIRST_PRODUCTS }), hint: t('fd.products.hint'),
      href: '/warehouse', done: i.products >= FIRST_PRODUCTS,
    },
    ...(profile ? [profile] : []),
    {
      key: 'cashier', label: t('fd.cashier'), hint: t('fd.cashier.hint'),
      href: '/staff', done: i.cashiers > 0,
    },
    {
      key: 'pin', label: t('fd.pin'), hint: t('fd.pin.hint'),
      href: '/settings?s=pin', done: !i.pinIsDefault,
    },
    {
      key: 'sale', label: t('fd.sale'), hint: t('fd.sale.hint'),
      href: '/pos', done: i.sales > 0,
    },
  ]
}

/** Show the checklist? A young shop, not dismissed, not finished — an open suggestion does not count. */
export function showFirstDay(i: FirstDayInput, dismissed: boolean): boolean {
  if (dismissed || i.sales >= YOUNG_SALES) return false
  return firstDaySteps(i).some((s) => !s.done && !s.optional)
}
